import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, VStack } from 'native-base';

import { ClockIcon, Text } from '@pimm/base';
import { Daypart, DopBlock } from '@pimm/services/lib/sms-tenants/types';
import { formatTimeSpan } from '../_helper';
import Section from './section';

const COLS = {
  num: 16,
  label: 48,
  subtitle: 72,
  time: 32,
} as const;

type DaypartsBlockProps = {
  dayparts?: Daypart[];
  dayblocks?: DopBlock[];
};

export const DaypartsBlock = ({ dayparts, dayblocks }: DaypartsBlockProps) => {
  const { t } = useTranslation();

  const sections = useMemo(() => {
    return [
      {
        key: 'dayblocks',
        titleKey: 'store_settings:day_blocks',
        columns: [
          { labelKey: 'store_settings:block', width: COLS.num },
          { labelKey: 'store_settings:title', width: COLS.label },
          { labelKey: 'store_settings:subtitle', width: COLS.subtitle },
          { labelKey: 'store_settings:start', width: COLS.time },
          { labelKey: 'store_settings:end', width: COLS.time },
        ],
        rows: dayblocks?.map(b => ({
          id: String(b.blockNumber),
          cells: [
            { value: b.blockNumber, width: COLS.num, bold: true },
            { value: b.title, width: COLS.label, bold: true },
            { value: b.subTitle, width: COLS.subtitle },
            { value: formatTimeSpan(b.startTime), width: COLS.time },
            { value: formatTimeSpan(b.endTime), width: COLS.time },
          ],
        })),
      },
      {
        key: 'dayparts',
        titleKey: 'store_settings:dayparts_section',
        columns: [
          { labelKey: 'store_settings:id', width: COLS.num },
          { labelKey: 'store_settings:label', width: COLS.label },
          { labelKey: 'store_settings:short', width: COLS.subtitle },
          { labelKey: 'store_settings:start', width: COLS.time },
          { labelKey: 'store_settings:end', width: COLS.time },
        ],
        rows: dayparts?.map(d => ({
          id: String(d.partNumber),
          cells: [
            { value: d.partNumber, width: COLS.num, bold: true },
            { value: d.label, width: COLS.label, bold: true },
            { value: d.shortTitle, width: COLS.subtitle },
            { value: formatTimeSpan(d.startTime), width: COLS.time },
            { value: formatTimeSpan(d.endTime), width: COLS.time },
          ],
        })),
      },
    ]
  }, [dayblocks, dayparts]); // prettier-ignore

  return (
    <Section.Card variant="form" title={t('store_settings:dayparts_and_day_blocks')} icon={<ClockIcon size={4} color="gray.700" />}>
      <VStack space={4}>
        {sections.map(section => {
          return (
            <VStack key={section.key} space={2}>
              <Text size="md" fontWeight={600} color="gray.700">
                {t(section.titleKey)}
              </Text>
              <Box w="full" rounded={10} borderWidth={1} borderColor="gray.200" overflow="hidden">
                <HStack bg="coolGray.50" borderBottomWidth={1} borderColor="gray.200" px={3} py={2}>
                  {section.columns.map((col, i) => (
                    <Text key={i} size="sm" fontWeight={600} color="gray.700" w={col.width}>
                      {t(col.labelKey)}
                    </Text>
                  ))}
                  <Box flex={1} />
                </HStack>

                {section.rows?.map((row, idx) => (
                  <Box key={row.id}>
                    {idx > 0 && <Box h="1px" bg="gray.100" />}
                    <HStack px={3} py={2} alignItems="center">
                      {row.cells.map((cell, i) => (
                        <Text
                          key={i}
                          size="md"
                          fontWeight={cell.bold ? 500 : undefined}
                          color={cell.bold ? 'gray.900' : 'gray.700'}
                          w={cell.width}
                        >
                          {String(cell.value ?? '')}
                        </Text>
                      ))}
                      <Box flex={1} />
                    </HStack>
                  </Box>
                ))}
              </Box>
            </VStack>
          );
        })}
      </VStack>
    </Section.Card>
  );
};
